"use client";

import clsx from "clsx";
import { forwardRef, InputHTMLAttributes, useState } from "react";
import { Switch } from "../Switch";

export type InputProps = {
  label?: string;
  description?: string;
  error?: string;
  block?: boolean;
  size?: "sm" | "md" | "lg";
  defaultHidden?: boolean;
  showSwitch?: boolean;
  defaultValue?: string | number | readonly string[] | null | undefined;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "defaultValue">;

const styles = {
  base: "antialiased inline-flex bg-white disabled:bg-gray-400 font-semibold text-gray-1200 placeholder:text-gray-900 border border-default hover:border-heavy whitespace-nowrap outline-none focus:ring-2 focus:ring-theme-accent ring-offset-1 transition duration-150 ease-in-out",
  size: {
    sm: "h-9 px-3 rounded-md",
    md: "h-10 px-4 rounded-md",
    lg: "h-12 px-4 rounded-md",
  },
  block: "flex w-full",
  error: "ring-2 ring-signal-error",
};

export const TextInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      error,
      block,
      size = "md",
      type = "text",
      autoComplete,
      autoFocus,
      disabled,
      placeholder,
      name,
      id,
      onChange,
      defaultHidden = false,
      showSwitch = false,
      defaultValue,
      ...props
    },
    ref,
  ) => {
    const [hidden, setHidden] = useState(defaultHidden);

    return (
      <div className={clsx([block && styles.block, "flex-col"])}>
        {label && (
          <div className="flex items-center justify-between mb-1">
            <label
              className="text-gray-1200 block text-sm font-medium"
              htmlFor={id ?? name}
            >
              {label}
            </label>

            {showSwitch && (
              <Switch
                checked={!hidden}
                onCheckedChange={hidden => setHidden(!hidden)}
              />
            )}
          </div>
        )}

        {!hidden && (
          <>
            <input
              type={type}
              id={id ?? name}
              name={name}
              disabled={disabled}
              autoComplete={autoComplete}
              placeholder={placeholder}
              autoFocus={autoFocus}
              defaultValue={defaultValue ?? undefined}
              className={clsx([
                styles.base,
                styles.size[size],
                error && styles.error,
                block && styles.block,
              ])}
              onChange={onChange}
              ref={ref}
              {...props}
            />
            {error && (
              <span className="text-signal-error mt-0.5 block text-sm font-medium">
                {error}
              </span>
            )}
            {!error && description && (
              <span className="text-gray-1000 mt-0.5 block text-sm">
                {description}
              </span>
            )}
          </>
        )}
      </div>
    );
  },
);

TextInput.displayName = "TextInput";
